import { computed, ref } from 'vue'
import { useApolloClient } from '@vue/apollo-composable'
import { UNIFIED_INBOX_QUERY } from '~/graphql/inbox/unified'
import { useAuthStore } from '@/stores/authStore'
import {
  adjustTotals,
  clearSignal,
  dismissRow,
  flipCallsHandled,
} from '@/utils/inboxOptimistic'

/**
 * THE INBOX — module-level singleton, one poll for the whole page.
 * ----------------------------------------------------------------
 * Feeds the unified page, where a card is a PERSON carrying both their unread
 * texts and their unseen notes. Same manners as the other pollers: silent
 * while the tab is hidden, never overlapping, optimistic removal on dismiss.
 */

/**
 * The strip in the navbar runs on this poll now (Sandy 2026-08-23: "lo más
 * realtime posible"), so it ticks faster than it did when only the inbox pages
 * watched it — and stops dead while the tab is hidden, so the cost is paid only
 * by someone actually looking.
 *
 * Coming BACK to the window is where the real gain is: `visibilitychange`
 * catches a tab switch and `focus` catches an app switch, and either one
 * refetches immediately instead of waiting out the tick. Websockets are the
 * eventual answer; until then this is the closest thing to live.
 */
const POLL_MS = 10_000

/** Don't refetch on focus if the last one was this recent. */
const FOCUS_MIN_GAP_MS = 3_000

const EMPTY_TOTALS = {
  missedCalls: 0,
  cases: 0,
  staff: 0,
  contacts: 0,
  unknown: 0,
  all: 0,
}

const _missedCalls = ref([])
const _cases = ref([])
const _staff = ref([])
const _contacts = ref([])
const _unknown = ref([])
const _totals = ref({ ...EMPTY_TOTALS })
const _loading = ref(false)
const _loaded = ref(false)

/**
 * Missed calls: 'unattended' (the working queue) or 'all' (plus the ones
 * already returned TODAY). Purely a client-side LENS since 2026-08-13 — the
 * poll always carries the whole day, and the page filters on `handled`.
 */
const _callScope = ref('unattended')

let _running = false
let _timer = null

/**
 * The request in flight, or null. A PROMISE rather than a boolean so a caller
 * that must not be dropped — switching the missed-calls view — can wait for the
 * current one and then run, instead of silently no-op'ing and leaving the list
 * showing the other scope until the next tick (Sandy 2026-08-12).
 */
let _inFlight = null
let _client = null
let _visibilityBound = false
let _lastFetchAt = 0

function _fetch(resolveClient, { force = false } = {}) {
  if (!resolveClient) return Promise.resolve()

  // A poll landing on top of another is pointless; a forced one waits its turn.
  if (_inFlight) return force ? _inFlight.then(() => _fetch(resolveClient)) : _inFlight

  _inFlight = _run(resolveClient)

  return _inFlight
}

async function _run(resolveClient) {
  if (!_loaded.value) _loading.value = true
  _lastFetchAt = Date.now()
  try {
    const { data } = await resolveClient().query({
      query: UNIFIED_INBOX_QUERY,
      variables: {
        noteLimit: 200,

        // ALWAYS the full day: pending calls plus the ones returned today.
        // The Unattended|All toggle filters CLIENT-side (Sandy 2026-08-13) —
        // one payload carries both counts, so the switch is instant and the
        // two numbers on it can never disagree with the cards.
        includeHandledCallsToday: true,
      },
      fetchPolicy: 'network-only',
    })

    const p = data?.getUnifiedInbox ?? {}

    _missedCalls.value = p.missedCalls ?? []
    _cases.value = p.cases ?? []
    _staff.value = p.staff ?? []
    _contacts.value = p.contacts ?? []
    _unknown.value = p.unknown ?? []
    _totals.value = { ...EMPTY_TOTALS, ...(p.totals ?? {}) }
    _loaded.value = true
  }
  catch { /* transient — the next tick retries */ }
  finally {
    _inFlight = null
    _loading.value = false
  }
}

function _startTimer() {
  if (_timer || !_running || !_client) return
  _timer = setInterval(() => _fetch(_client), POLL_MS)
}

function _stopTimer() {
  if (_timer) {
    clearInterval(_timer)
    _timer = null
  }
}

function _onVisibility() {
  if (document.hidden) {
    _stopTimer()

    return
  }
  if (!_running) return
  _fetch(_client)
  _startTimer()
}

/**
 * Window focus — the app-switch case `visibilitychange` doesn't reliably cover.
 * Rate-limited, because focus fires on things as small as dismissing a dialog.
 */
function _onFocus() {
  if (!_running || (typeof document !== 'undefined' && document.hidden)) return
  if (Date.now() - _lastFetchAt < FOCUS_MIN_GAP_MS) return

  _fetch(_client)
}

/** Reset on logout so the next user never inherits this badge. */
export function resetUnifiedInbox() {
  _running = false
  _stopTimer()

  if (_visibilityBound && typeof document !== 'undefined') {
    document.removeEventListener('visibilitychange', _onVisibility)
    window.removeEventListener('focus', _onFocus)
    _visibilityBound = false
  }

  _client = null
  _missedCalls.value = []
  _cases.value = []
  _staff.value = []
  _contacts.value = []
  _unknown.value = []
  _totals.value = { ...EMPTY_TOTALS }
  _loaded.value = false
  _callScope.value = 'unattended'
}

export function useUnifiedInbox() {
  const { resolveClient } = useApolloClient()
  const authStore = useAuthStore()

  function start() {
    if (!authStore.isLeadAgencyUser && !authStore.isSysAdmin) return

    _client = resolveClient
    if (_running) return
    _running = true

    if (typeof document !== 'undefined' && !_visibilityBound) {
      document.addEventListener('visibilitychange', _onVisibility)
      window.addEventListener('focus', _onFocus)
      _visibilityBound = true
    }

    _fetch(_client)
    _startTimer()
  }

  function refresh() {
    if (authStore.isLeadAgencyUser || authStore.isSysAdmin) {
      _fetch(_client ?? resolveClient)
    }
  }

  /**
   * What a PAGE calls on mount (Sandy 2026-08-18) — "make sure this is polling
   * and that what I'm about to render is current".
   *
   * The four inbox pages share this one poll, and the sidebar starts it at
   * login, so a page arriving is almost never the thing that starts it: it
   * just needs data newer than up-to-`POLL_MS`-old. Calling `start()` and
   * `refresh()` back to back did that, but on the ONE mount that really is
   * first it fired two identical queries a millisecond apart.
   *
   * Deliberately NOT folded into `start()`: the sidebar calls that on every
   * nav recompute, and making it refetch would turn menu re-renders into
   * network traffic.
   */
  function ensureFresh() {
    const wasRunning = _running

    start()
    if (wasRunning) refresh()
  }

  /**
   * Optimistic removal — the click drops the row and its count NOW; the
   * mutation + refresh that follow true it up.
   */
  function dismiss(section, match) {
    const lists = {
      missedCalls: _missedCalls,
      cases: _cases,
      staff: _staff,
      contacts: _contacts,
      unknown: _unknown,
    }

    const list = lists[section]
    if (!list) return

    const { rows, dropped, matched } = dismissRow(list.value, match)
    if (!matched) return

    list.value = rows
    _totals.value = adjustTotals(_totals.value, section, -dropped)
  }

  /**
   * Optimistically clear ONE SIGNAL on an entity's row (the split case
   * sections mark notes and messages independently — Sandy 2026-08-14).
   * The row only leaves the list when BOTH signals are quiet; otherwise it
   * stays for the section still showing its other signal.
   */
  function clearCardSignal(section, entityId, signal) {
    const lists = { cases: _cases, staff: _staff, contacts: _contacts }
    const list = lists[section]
    if (!list) return

    const { rows, dropped } = clearSignal(list.value, entityId, signal)
    if (!dropped) return

    list.value = rows
    _totals.value = adjustTotals(_totals.value, section, -dropped)
  }

  /** Switch the missed-calls lens. No refetch: the data is already here. */
  function setCallScope(scope) {
    _callScope.value = scope === 'all' ? 'all' : 'unattended'
  }

  /**
   * Optimistically flip calls handled/unhandled IN PLACE. Rows are never
   * removed here — in the All lens a returned call stays visible, greyed —
   * and the pending badge follows the flip.
   */
  function setCallsHandled(recordIds, handled) {
    const { rows, delta } = flipCallsHandled(_missedCalls.value, recordIds, handled)

    _missedCalls.value = rows
    _totals.value = adjustTotals(_totals.value, 'missedCalls', delta)
  }

  return {
    callScope: computed(() => _callScope.value),
    setCallScope,
    setCallsHandled,
    missedCalls: computed(() => _missedCalls.value),
    cases: computed(() => _cases.value),
    staff: computed(() => _staff.value),
    contacts: computed(() => _contacts.value),
    unknown: computed(() => _unknown.value),
    totals: computed(() => _totals.value),
    total: computed(() => _totals.value.all ?? 0),
    loading: computed(() => _loading.value),
    loaded: computed(() => _loaded.value),
    start,
    ensureFresh,
    refresh,
    dismiss,
    clearCardSignal,
    stop: resetUnifiedInbox,
  }
}
